import { useCallback, useEffect, useRef } from "react";

const RECAPTCHA_SITE_KEY = import.meta.env.VITE_RECAPTCHA_SITE_KEY;
const SCRIPT_ID = "recaptcha-enterprise-script";

function loadScript(siteKey) {
	const existingScript = document.getElementById(SCRIPT_ID);
	if (existingScript) {
		return Promise.resolve();
	}

	return new Promise((resolve, reject) => {
		const script = document.createElement("script");
		script.id = SCRIPT_ID;
		script.src = `https://www.google.com/recaptcha/enterprise.js?render=${siteKey}`;
		script.async = true;
		script.defer = true;
		script.onload = () => resolve();
		script.onerror = () => reject(new Error("Failed to load reCAPTCHA script"));
		document.head.appendChild(script);
	});
}

/**
 * Loads Google reCAPTCHA Enterprise and returns a function to obtain a token
 * for a given action right before submitting a form.
 */
export default function useRecaptchaEnterprise() {
	const scriptLoadedRef = useRef(null);

	useEffect(() => {
		if (!RECAPTCHA_SITE_KEY) return;
		scriptLoadedRef.current = loadScript(RECAPTCHA_SITE_KEY);
	}, []);

	const getToken = useCallback(async (action = "submit") => {
		if (!RECAPTCHA_SITE_KEY) {
			console.warn("VITE_RECAPTCHA_SITE_KEY is not configured, skipping captcha.");
			return null;
		}

		if (!scriptLoadedRef.current) {
			scriptLoadedRef.current = loadScript(RECAPTCHA_SITE_KEY);
		}
		await scriptLoadedRef.current;

		return new Promise((resolve, reject) => {
			window.grecaptcha.enterprise.ready(async () => {
				try {
					const token = await window.grecaptcha.enterprise.execute(
						RECAPTCHA_SITE_KEY,
						{ action },
					);
					resolve(token);
				} catch (error) {
					reject(error);
				}
			});
		});
	}, []);

	return { getToken };
}
